import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { AccessControlError, withSentryContext } from '@/api/errors';
import { missingTabPermissions, readableTabs } from '@/components/AccessManagement/tabs';
import { checkFeatureGateOAuth } from '@/helpers/statsig/featureGate';
import { head } from '@/router/Head';
import { resolveAccountPermissions } from '@/router/resolvers/user';

/**
 * Layout of the access-management subtree: renders the tab bar, and forwards the bare `/cloud-access`
 * URL to the first tab the account may read. It does no access checking of its own — each tab gates
 * itself by fetching the data it needs anyway, and the `AccessControlError` the permissions interceptor
 * throws bubbles up to the nearest `notFoundComponent` as "Access Denied".
 */
export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/cloud-access')({
  staticData: {
    title: 'Access Management',
    // Owns the access-management subtree (roles, management-keys, all-users, …); children inherit.
    domain: 'iam',
  },

  beforeLoad: async ({ context, params, matches }) => {
    // Only the bare URL needs forwarding: entering through a child means a tab is already selected.
    if (matches.at(-1)?.routeId !== Route.id) {
      return;
    }

    // The account layout already awaited `ensureStatsigUserFeatureGateReady`.
    const gates = { oauth: checkFeatureGateOAuth(context.statsigClient) };
    const held = await resolveAccountPermissions({ queryClient: context.queryClient, accountId: params.accountId });
    const [tab] = readableTabs(held, gates);

    if (!tab) {
      // Nowhere to forward to, so the subtree refuses itself with the same error a gated call raises.
      // Thrown in `beforeLoad` it never flows through the QueryClient's onError pipeline, so
      // `deriveSentryContext` never runs. Demote it here the way that pipeline would: an expected
      // product state, not a failure.
      const missing = missingTabPermissions(held, gates);

      throw withSentryContext(new AccessControlError(missing), {
        level: 'warning',
        extra: { missing_permissions: missing },
      });
    }

    throw redirect({ to: tab.entrypoint ?? tab.to, params, replace: true });
  },

  head,
  component: lazyRouteComponent(() => import('./route.component'), 'CloudAccessComponent'),
});
